/* eslint-disable @typescript-eslint/no-unused-vars */
import { TRIVIA_ANSWER, TRIVIA_LIST, TRIVIA_PLAY, TRIVIA_SET_INTEREST, TRIVIA_VALIDATE } from 'constants/api'
import axios from 'axios'
import React, { Dispatch, FC, useContext, useReducer, useEffect } from 'react'
import equals from 'lodash.isequal'
import { useHistory } from 'react-router-dom'
import textServices from 'services/text-services'
import { usePopupContext } from 'hooks/use-main-cass'
import PopUpExchangeV2 from 'components/exchange/popUpExchange'
import CONFIGS from 'build/Config'
import { Prize } from 'views/prizes/types'
import { PrizesCouponType } from 'types/prizes.d'
import { F1_TRIVIA_PRIZE_FALLBACK_IMAGE, isF1Trivia } from 'views/pitstops/constants'
import { useSession } from '../session-context'
import reducer, { TriviaAction, TriviaActionsType, TriviaState } from './reducer'
export interface TriviaReducer {
  state: TriviaState
  dispatch: Dispatch<TriviaAction>
}

const TriviaContext = React.createContext<TriviaReducer>({} as TriviaReducer)

const TriviaProvider: FC = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, {
    interests: []
  } as TriviaState)

  return <TriviaContext.Provider value={{ state, dispatch }}>{children}</TriviaContext.Provider>
}

export const useTrivia = () => {
  const history = useHistory()
  const { openPopup, closePopup } = usePopupContext()
  const [{ data: session }] = useSession()
  const [{ user }, { setUser, setPoints }] = useSession()

  const { state, dispatch } = useContext(TriviaContext)
  const version = CONFIGS.versionPh || 'v1'

  useEffect(() => {
    if (user) {
      const mappedInterests = user?.interests.map(({ id }) => id)
      const contentChanged = !equals(mappedInterests, state.interests)

      if (contentChanged) {
        dispatch({
          type: TriviaActionsType.SET_INTERESTS,
          payload: mappedInterests
        })
      }
    }
  }, [user])

  const setError = (err: any) => {
    dispatch({
      type: TriviaActionsType.SET_ERROR,
      payload: err
    })
  }

  const setInterests = async (interests: TriviaState['interests']) => {
    try {
      const response = await axios.put(TRIVIA_SET_INTEREST, interests, {
        headers: {
          Authorization: `Bearer ${((session as any)?._tokenResponse as any)?.idToken}`,
          'Ph-Api-Id': PH_API_ID ?? 1
        }
      })

      setUser(response.data)

      dispatch({
        type: TriviaActionsType.SET_INTERESTS,
        payload: interests
      })
    } catch (error) {
      console.error(error)
    }
  }

  const getTrivias = async () => {
    try {
      const response = await axios.get(TRIVIA_LIST, {
        headers: {
          Authorization: `Bearer ${((session as any)?._tokenResponse as any)?.idToken}`,
          'Ph-Api-Id': PH_API_ID ?? 1
        }
      })

      if (response.data) {
        dispatch({
          type: TriviaActionsType.SET_TRIVIAS,
          payload: response.data
        })

        return response.data
      }

      return response.data
    } catch (e) {
      console.error(e)
    }
  }

  const validateTrivia = async (triviaId: string | number, isDirectAccess: boolean) => {
    try {
      const response = await axios.post(
        TRIVIA_VALIDATE.replace(':triviaId', triviaId.toString()).replace(':isdirectaccess', isDirectAccess.toString()),
        undefined,
        {
          headers: {
            Authorization: `Bearer ${((session as any)?._tokenResponse as any)?.idToken}`,
            'Ph-Api-Id': PH_API_ID ?? 1
          }
        }
      )

      if (response.data) {
        dispatch({
          type: TriviaActionsType.SET_TRIVIA,
          payload: response.data
        })

        return true
      }

      return false
    } catch (error) {
      console.error('validate err', error.response?.data.code)
      setError(error.response?.data.code)
      throw error
    }
  }

  const playTrivia = async (triviaId: number | string, isDirectAccess: boolean) => {
    try {
      const response = await axios.put(
        TRIVIA_PLAY.replace(':triviaId', triviaId.toString()).replace(':isdirectaccess', isDirectAccess.toString()),
        undefined,
        {
          headers: {
            Authorization: `Bearer ${((session as any)?._tokenResponse as any)?.idToken}`,
            'Ph-Api-Id': PH_API_ID ?? 1
          }
        }
      )

      if (response.data.question) {
        dispatch({
          type: TriviaActionsType.SET_QUESTIONS,
          payload: response.data.question
        })

        return true
      }

      if (response.data.prize) {
        setPoints(response.data.prize.points)

        dispatch({
          type: TriviaActionsType.SET_PRIZE,
          payload: response.data.prize
        })

        return false
      }
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  const answerTrivia = async (triviaId: number | string, questionId: number | string, optionId: number | string) => {
    try {
      const response = await axios.put(
        TRIVIA_ANSWER.replace(':triviaId', triviaId.toString()),
        { triviaId, questionId, optionId },
        {
          headers: {
            Authorization: `Bearer ${((session as any)?._tokenResponse as any)?.idToken}`,
            'Ph-Api-Id': PH_API_ID ?? 1
          }
        }
      )

      if (response.data.question) {
        dispatch({
          type: TriviaActionsType.SET_QUESTIONS,
          payload: response.data.question
        })

        return true
      }

      if (response.data.prize) {
        setPoints(response.data.prize.points)
        if (version === 'v2') {
          const points = (response.data.prize as Prize)?.points
          const title = (response.data.prize as Prize)?.title
          let description = `Has respondido las preguntas correctamente y ganaste ${points ? `${points} ${POINTS_NAME}` : title}. ¡sigue así!`

          if ((response.data.prize as Prize)?.couponTypeId === PrizesCouponType.STICKER) {
            description = `Has respondido las preguntas correctamente y ganaste ${points} ${POINTS_NAME}. Además consigues el sello de ${title}, ¡sigue así!`
          }

          // F1 Pit Stops trivia only (campaign copy, Figma 5558:2901). The
          // title goes as a prop because the v2 popup opens without a
          // `module`, so lang keys cannot target it without touching every
          // other trivia of the theme.
          let f1Title: JSX.Element | undefined
          if (isF1Trivia(triviaId)) {
            description = textServices.findByText(
              'pitstops/trivia/winDescription',
              'Has contestado todas las preguntas correctamente. ¡Participa de los demás retos y podrás ganar un acceso al Paddock o Foro Sol!'
            ) as string
            f1Title = (
              <>
                ¡{textServices.findByText('pitstops/trivia/winGreeting', 'Venga,')} {user?.firstName}!
              </>
            )

            // Campaign prize art fallback: the admin prize image can be
            // missing or broken (edited after E2E). Probe it and fall back
            // to the CDN instant-win card; on a slow probe keep the
            // original so the popup never stalls.
            const prizeImage = (response.data.prize as Prize)?.imageUrl
            const imageOk = prizeImage
              ? await Promise.race([
                new Promise<boolean>((resolve) => {
                  const probe = new Image()
                  probe.onload = () => resolve(true)
                  probe.onerror = () => resolve(false)
                  probe.src = prizeImage
                }),
                new Promise<boolean>((resolve) => setTimeout(() => resolve(true), 2500))
              ])
              : false
            if (!imageOk) {
              (response.data.prize as Prize).imageUrl = F1_TRIVIA_PRIZE_FALLBACK_IMAGE
            }
          }

          return openPopup(
            {
              onClose: () => {
                history.push(response.data?.urlRedirect)
                closePopup()
              },
              content: <PopUpExchangeV2
                title={f1Title}
                description={description}
                buttons={{
                  handleButtonCommon() {
                    history.push(response.data?.urlRedirect)
                    closePopup()
                  },
                  handleButtonSticker() {
                    history.push(response.data?.urlRedirect)
                    closePopup()
                  },
                  text: response?.data?.buttonText
                }}
                buttonSecondary={{
                  text: '',
                  url: '',
                  hidden: true
                }}
                buttonTertiary={{
                  onClick() {
                    closePopup()
                  },
                  hidden: false,
                  text: response?.data?.buttonText2,
                  url: response?.data?.urlRedirect2
                }}
                item={response.data.prize}
                useRanking={response?.data?.withRanking}
              />
            }
          )
        }

        if (response.data?.urlRedirect) {
          dispatch({
            type: TriviaActionsType.SET_CONGRATS,
            payload: {
              urlRedirect: response.data?.urlRedirect,
              buttonText: response?.data?.buttonText,
              urlRedirect2: response?.data?.urlRedirect2,
              buttonText2: response?.data?.buttonText2,
              profileTime: response?.data?.profileRanking?.time,
              useRanking: response?.data?.withRanking
            }
          })
        }

        dispatch({
          type: TriviaActionsType.SET_PRIZE,
          payload: response.data.prize
        })

        return false
      }

      return true
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  const resetTrivia = () => {
    // setPoints(points)

    dispatch({
      type: TriviaActionsType.RESET,
      payload: null
    })
  }

  const exchangePrize = (points: number) => {
    history.push(
      state?.congrats?.urlRedirect ?? textServices.findByText('trivias/exchange/button/url', '/profile/history')
    )
  }

  const methods = {
    setInterests,
    getTrivias,
    validateTrivia,
    resetTrivia,
    exchangePrize,
    setError,
    playTrivia,
    answerTrivia
  }

  const values = [state, methods] as [TriviaState, typeof methods]

  return values
}

export default TriviaProvider
